import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';

/**
 * Cart updates from quick add that are still in flight. Quick add shows its result before the server has
 * answered, so a customer can click the cart link first. Leaving the page then drops the request or builds
 * the cart page without the item, so cart links wait for these to finish.
 * @type {Set<Promise<Response>>}
 */
const pendingCartUpdates = new Set();

document.addEventListener(
  'click',
  async (event) => {
    if (pendingCartUpdates.size === 0) return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (!(event.target instanceof Element)) return;

    const link = event.target.closest('a[href]');
    if (!(link instanceof HTMLAnchorElement) || link.target === '_blank') return;
    if (link.origin !== window.location.origin || link.pathname !== '/cart') return;

    event.preventDefault();
    await Promise.allSettled([...pendingCartUpdates]);
    window.location.assign(link.href);
  },
  true
);

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {string | null} */
  #variantId = null;
  /** @type {HTMLButtonElement | null} */
  #minusBtn = null;
  /** @type {HTMLButtonElement | null} */
  #plusBtn = null;
  /** @type {number | null} */
  #pendingUpdate = null;
  /** @type {boolean} */
  #isOptimisticUpdate = false;

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const productLink = productCard?.getProductCardLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  /**
   * Handles cart update events to update quantity visibility
   * @param {CartUpdateEvent} event - The cart update event
   */
  #handleCartUpdate = (event) => {
    // Ignore events we dispatched ourselves to prevent double-updates
    if (event.target === this) return;

    // Ignore all events during optimistic update window
    if (this.#isOptimisticUpdate) return;

    // Cancel any pending update to avoid redundant DOM operations
    if (this.#pendingUpdate) {
      cancelAnimationFrame(this.#pendingUpdate);
    }

    // Schedule update for next frame to batch DOM mutations and avoid mid-transition updates
    this.#pendingUpdate = requestAnimationFrame(() => {
      this.#updateQuantityVisibility(event.detail?.resource);
      this.#updateQuantityBadge(event.detail?.resource);
      this.#pendingUpdate = null;
    });
  };

  /**
   * Updates the quantity badge on the choose button for multi-variant products
   * @param {Object} [cart] - Optional cart object to use for quantity lookup
   */
  #updateQuantityBadge(cart) {
    const chooseButton = this.querySelector('.quick-add__button--choose');
    if (!chooseButton) return;

    const productId = chooseButton.dataset.productId;
    if (!productId) return;

    // Calculate total quantity for this product from cart
    let totalQuantity = 0;
    const cartObj = /** @type {any} */ (cart);

    if (cartObj?.items) {
      // Calculate from provided cart object
      for (const item of cartObj.items) {
        if (item.product_id.toString() === productId) {
          totalQuantity += item.quantity;
        }
      }
    } else if (window.Shopify?.cart?.items) {
      // Calculate from global cart object
      // @ts-ignore
      for (const item of window.Shopify.cart.items) {
        if (item.product_id.toString() === productId) {
          totalQuantity += item.quantity;
        }
      }
    } else {
      // Fallback to the data attribute
      totalQuantity = parseInt(chooseButton.dataset.totalInCart || '0', 10);
    }

    // Update data attribute
    chooseButton.dataset.totalInCart = totalQuantity.toString();

    // Update or create badge element
    let badge = chooseButton.querySelector('.quick-add__quantity-badge');

    if (totalQuantity > 0) {
      chooseButton.classList.add('has-cart-items');
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'quick-add__quantity-badge';
        chooseButton.appendChild(badge);
      }
      badge.textContent = totalQuantity.toString();
      badge.style.display = '';
    } else {
      chooseButton.classList.remove('has-cart-items');
      if (badge) {
        badge.style.display = 'none';
      }
    }

    // Set visibility attribute for multi-variant products (so button stays visible when items in cart)
    this.toggleAttribute('data-quantity-visible', totalQuantity > 0);
  }

  /**
   * Handles variant update events to update the variant ID and quantity visibility
   * @param {import('@theme/events').VariantUpdateEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    // Update variant ID if it changed
    const variantInput = this.querySelector('input[name="id"]');
    if (variantInput instanceof HTMLInputElement) {
      const newVariantId = variantInput.value;
      if (newVariantId !== this.#variantId) {
        this.#variantId = newVariantId;
        this.#updateQuantityVisibility();
      }
    }
  };

  /**
   * Sets up event listeners for quantity control buttons
   */
  #setupQuantityControls() {
    // Clean up any existing listeners first
    this.#cleanupQuantityControls();

    const controlShell = this.querySelector('.quick-add__control-shell') ?? this;

    this.#minusBtn = controlShell.querySelector('.quick-add__qty-minus');
    this.#plusBtn = controlShell.querySelector('.quick-add__qty-plus');

    if (this.#minusBtn) {
      this.#minusBtn.addEventListener('click', this.#handleQuantityDecrease);
    }
    if (this.#plusBtn) {
      this.#plusBtn.addEventListener('click', this.#handleQuantityIncrease);
    }
  }

  /**
   * Cleans up event listeners for quantity control buttons
   */
  #cleanupQuantityControls() {
    if (this.#minusBtn) {
      this.#minusBtn.removeEventListener('click', this.#handleQuantityDecrease);
      this.#minusBtn = null;
    }
    if (this.#plusBtn) {
      this.#plusBtn.removeEventListener('click', this.#handleQuantityIncrease);
      this.#plusBtn = null;
    }
  }

  /**
   * Handles quantity decrease
   * @param {Event} event - The click event
   */
  #handleQuantityDecrease = (event) => {
    event.preventDefault();
    event.stopPropagation();
    this.#updateCartQuantity(-1);
  };

  /**
   * Handles quantity increase
   * @param {Event} event - The click event
   */
  #handleQuantityIncrease = (event) => {
    event.preventDefault();
    event.stopPropagation();
    this.#updateCartQuantity(1);
  };

  /**
   * Updates the cart quantity for this variant
   * @param {number} delta - The change in quantity (+1 or -1)
   */
  async #updateCartQuantity(delta) {
    if (!this.#variantId) return;

    const currentQuantity = this.#getCartQuantityForVariant(this.#variantId);
    const newQuantity = Math.max(0, currentQuantity + delta);

    if (newQuantity === currentQuantity) return; // No change needed

    // Set optimistic update flag to prevent event handler from running during this window
    this.#isOptimisticUpdate = true;

    // Show an optimistic state immediately so the UI doesn't feel unresponsive on touch devices
    this.#applyOptimisticQuantity(newQuantity);

    try {
      const formData = new FormData();
      formData.append(`updates[${this.#variantId}]`, newQuantity.toString());

      const request = fetch(`${Theme.routes.cart_update_url}`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
        },
        body: formData,
      });

      pendingCartUpdates.add(request);
      let response;
      try {
        response = await request;
      } finally {
        pendingCartUpdates.delete(request);
      }

      if (!response.ok) {
        throw new Error(`Cart update failed: ${response.status}`);
      }

      const cartData = await response.json();
      if (cartData && typeof cartData === 'object') {
        // Keep Shopify cart reference in sync for other components that read from it
        if (typeof window !== 'undefined') {
          // @ts-ignore - Shopify namespace may not be typed
          window.Shopify = window.Shopify || {};
          // @ts-ignore - Shopify namespace may not be typed
          window.Shopify.cart = cartData;
        }

        const itemCount = typeof cartData.item_count === 'number' ? cartData.item_count : undefined;
        const sourceId = this.id || this.getAttribute('id') || this.closest('[id]')?.id || 'quick-add-component';

        // Clear optimistic flag BEFORE dispatching event so other components can process it
        this.#isOptimisticUpdate = false;

        this.dispatchEvent(
          new CartUpdateEvent(cartData, sourceId, {
            itemCount,
            variantId: this.#variantId,
            source: 'quick-add-component',
          })
        );

        // Update quantity badge with new cart data
        this.#updateQuantityBadge(cartData);
      }
    } catch (error) {
      console.error('Failed to update cart quantity:', error);
      // Clear optimistic flag
      this.#isOptimisticUpdate = false;
      // Revert optimistic UI if the request failed
      this.#updateQuantityVisibility();
    }
  }

  /**
   * Updates the quantity visibility based on cart contents
   * @param {Object} [cart] - Optional cart object to use for quantity lookup
   */
  #updateQuantityVisibility(cart) {
    if (!this.#variantId) return;

    // Get the current cart quantity for this variant
    const cartQuantity = this.#getCartQuantityForVariant(this.#variantId, cart);
    const hasQuantity = cartQuantity > 0;

    // Update data-quantity-visible attribute
    this.toggleAttribute('data-quantity-visible', hasQuantity);

    // Update quantity-persist class on buttons
    const controlShell = this.querySelector('.quick-add__control-shell') ?? this;

    const addButton = controlShell.querySelector('.quick-add__button--add') ??
                      controlShell.querySelector('.quick-add__button');
    if (addButton) {
      addButton.classList.toggle('quantity-persist', hasQuantity);
      // Set data-quantity-visible on the button itself for CSS selector matching
      addButton.toggleAttribute('data-quantity-visible', hasQuantity);
    }

    // Update the quantity display
    const qtyValue = controlShell.querySelector('.quick-add__qty-value');
    if (qtyValue) {
      qtyValue.textContent = cartQuantity.toString();
      qtyValue.setAttribute('data-cart-quantity', cartQuantity.toString());
    }

    // Update the data-quantity-empty attribute on the button (reuse same button reference)
    if (addButton) {
      if (hasQuantity) {
        addButton.removeAttribute('data-quantity-empty');
      } else {
        addButton.setAttribute('data-quantity-empty', '');
      }
    }

    // Update quantity control button states
    const minusBtn = controlShell.querySelector('.quick-add__qty-minus');
    const plusBtn = controlShell.querySelector('.quick-add__qty-plus');

    if (minusBtn) {
      minusBtn.setAttribute('aria-disabled', cartQuantity <= 0 ? 'true' : 'false');
    }
    if (plusBtn) {
      // For plus button, we could check inventory limits, but for now just enable it
      plusBtn.setAttribute('aria-disabled', 'false');
    }
  }

  /**
   * Gets the cart quantity for a specific variant
   * @param {string} variantId - The variant ID to check
   * @param {Object} [cart] - Optional cart object to use
   * @returns {number} The quantity in cart
   */
  #getCartQuantityForVariant(variantId, cart) {
    // Check if we have a quantity value element
    
    // 1. Prefer the provided cart object (from event)
    const cartObj = /** @type {any} */ (cart);
    if (cartObj?.items) {
      const item = cartObj.items.find((/** @type {any} */ item) => item.variant_id.toString() === variantId);
      return item ? item.quantity : 0;
    }

    // 2. Prefer the global cart object if available (kept in sync by cart components)
    // @ts-ignore - Shopify global may not be fully typed
    if (window.Shopify?.cart?.items) {
      // @ts-ignore
      const item = window.Shopify.cart.items.find((/** @type {any} */ item) => item.variant_id.toString() === variantId);
      if (item) {
        return item.quantity;
      }
    }

    // 3. Check if we have a stored value but no global cart (rare edge case, maybe initial load failed)
    // If we really need to verify, we could fetch here, but better to rely on events.
    
    // Fallback: use the element's stored data attribute
    const controlShell = this.querySelector('.quick-add__control-shell') ?? this;
    const qtyValue = controlShell.querySelector('.quick-add__qty-value[data-cart-quantity]') ??
      this.querySelector('.quick-add__qty-value[data-cart-quantity]');
    if (qtyValue) {
      const qty = parseInt(qtyValue.getAttribute('data-cart-quantity') || '0');
      if (!isNaN(qty)) return qty;
    }

    return 0;
  }

  /**
   * Applies an optimistic quantity to the UI before the server confirms
   * @param {number} quantity
   */
  #applyOptimisticQuantity(quantity) {
    if (!this.#variantId) return;

    const cartStub = {
      items: [
        {
          variant_id: this.#variantId,
          quantity,
        },
      ],
    };

    this.#updateQuantityVisibility(cartStub);
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);

    // Get variant ID from the form
    const variantInput = this.querySelector('input[name="id"]');
    if (variantInput instanceof HTMLInputElement) {
      this.#variantId = variantInput.value;
    }

    // Add event listeners for quantity controls
    // We need to use capture phase or delegate to a parent because these elements might be
    // removed/re-added during morph operations or initial render timing.
    // However, since we are looking them up here, they should exist.
    // The issue might be that other listeners (like on:click handlers) are capturing the event first.
    
    // Attach a capture listener at the component root so we intercept before forms/buttons
    this.addEventListener('click', this.#handleShellClick, true);
    
    // Stop propagation for all Quick Add button clicks to prevent product card navigation
    // This is especially important on mobile where click events bubble to the product card
    // Use capture phase so we can intercept before the form submission runs
    this.addEventListener('click', this.#handleQuickAddButtonClick, true);

    // Add form submit listener to prevent form submission for Quick Add buttons
    // We attach to window in capture phase to ensure we run BEFORE the framework's 
    // document-level event delegation (component.js)
    window.addEventListener('submit', this.#handleFormSubmit, true);

    // Ensure individual +/- buttons still have listeners as fallback
    this.#setupQuantityControls();

    // Initial check for cart quantity
    this.#updateQuantityVisibility();

    // Initial check for quantity badge (multi-variant products)
    this.#updateQuantityBadge();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    
    this.removeEventListener('click', this.#handleShellClick, true);
    this.removeEventListener('click', this.#handleQuickAddButtonClick, true);
    window.removeEventListener('submit', this.#handleFormSubmit, true);

    // Cancel any pending RAF update
    if (this.#pendingUpdate) {
      cancelAnimationFrame(this.#pendingUpdate);
      this.#pendingUpdate = null;
    }

    this.#cleanupQuantityControls();
    this.#abortController?.abort();
  }

  /**
   * Handles clicks within the control shell to delegate quantity actions
   * @param {Event} event
   */
  #handleShellClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const controlShell = event.target.closest('.quick-add__control-shell');
    if (!controlShell || !this.contains(controlShell)) return;

    const target = /** @type {Element} */ (event.target);
    
    // Check for minus button
    const minusBtn = target.closest('.quick-add__qty-minus');
    if (minusBtn) {
      event.preventDefault();
      event.stopPropagation();
      this.#updateCartQuantity(-1);
      return;
    }
    
    // Check for plus button
    const plusBtn = target.closest('.quick-add__qty-plus');
    if (plusBtn) {
      event.preventDefault();
      event.stopPropagation();
      this.#updateCartQuantity(1);
      return;
    }
  };

  /**
   * Handles clicks on Quick Add buttons to prevent event bubbling to product card
   * This is critical on mobile where click events would otherwise trigger navigation
   * @param {Event} event
   */
  #handleQuickAddButtonClick = (event) => {
    if (!(event.target instanceof Element)) return;

    // Only care about add buttons inside this quick add component
    const quickAddButton = event.target.closest('.quick-add__button--add');
    if (!quickAddButton || !this.contains(quickAddButton)) return;

    // Always stop propagation so the product card doesn't navigate
    event.stopPropagation();
    
    // For Quick Add buttons that are submit buttons, always prevent form submission
    // This ensures consistent behavior regardless of cart state - we use AJAX instead
    if (quickAddButton instanceof HTMLButtonElement && quickAddButton.type === 'submit') {
      // Always prevent default form submission for Quick Add buttons
      event.preventDefault();
      
      // Get variant ID from form input if not already set
      const variantInput = this.querySelector('input[name="id"]');
      const variantId = variantInput instanceof HTMLInputElement ? variantInput.value : this.#variantId;
      
      // If we don't have a variant ID, we've still prevented form submission
      // but can't proceed with AJAX - this is fine, prevents unwanted navigation
      if (!variantId) return;
      
      // Check if item is already in cart by checking current quantity
      const currentQuantity = this.#getCartQuantityForVariant(variantId);
      
      // If quantity is 0, add the item to cart (this will show the quantity selector)
      // If quantity > 0, the quantity selector should already be visible
      // In both cases, we've prevented form submission to ensure consistent behavior
      if (currentQuantity === 0) {
        // Ensure variantId is set before calling updateCartQuantity
        if (!this.#variantId && variantId) {
          this.#variantId = variantId;
        }
        this.#updateCartQuantity(1);
      }
      // Note: If quantity > 0, we've prevented form submission but don't need to do anything
      // The quantity selector should already be visible for the user to adjust quantity
    }
  };

  /**
   * Handles form submit events to prevent form submission for Quick Add buttons
   * This is critical because submit buttons trigger both click AND submit events
   * @param {Event} event - The submit event
   */
  #handleFormSubmit = (event) => {
    // Ensure the submit event originated from this component
    if (!this.contains(event.target)) return;

    // Find the submit button that triggered this form submission
    // Note: event.submitter is supported in modern browsers
    const submitButton = event.submitter || 
      this.querySelector('.quick-add__button--add[data-quantity-empty]');
    
    // Only intercept if this is a Quick Add button with data-quantity-empty
    // This means it's the initial add (quantity 0), and we want to handle it via AJAX
    if (submitButton && submitButton.hasAttribute('data-quantity-empty')) {
      event.preventDefault();
      event.stopPropagation();
      
      // Get variant ID from form input
      const variantInput = this.querySelector('input[name="id"]');
      const variantId = variantInput instanceof HTMLInputElement ? variantInput.value : this.#variantId;
      
      if (!variantId) return; // Can't proceed without variant ID
      
      // Ensure variantId is set before calling updateCartQuantity
      if (!this.#variantId && variantId) {
        this.#variantId = variantId;
      }
      
      // Add item to cart via AJAX (this will show the quantity selector)
      this.#updateCartQuantity(1);
    }
    // Otherwise, allow normal form submission (for edge cases)
  };

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const currentUrl = this.productPageUrl;
    const cacheKey = `${currentUrl}:bulk`;

    // Check if we have cached bulk-order content for this URL
    let bulkOrderContent = this.#cachedContent.get(cacheKey);

    if (!bulkOrderContent) {
      // Fetch bulk-order content via section rendering
      bulkOrderContent = await this.#fetchBulkOrderContent();
      if (bulkOrderContent) {
        this.#cachedContent.set(cacheKey, bulkOrderContent);
      }
    }

    if (bulkOrderContent) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (bulkOrderContent.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#openQuickAddModal();
    } else {
      // Fallback: open product page in same tab if bulk-order fetch failed
      window.location.href = currentUrl;
    }
  };

  /**
   * Fetches the bulk-order content via section rendering
   * @returns {Promise<Element | null>}
   */
  async #fetchBulkOrderContent() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const productLink = productCard?.getProductCardLink();

    if (!productLink?.href) return null;

    const url = new URL(productLink.href);
    // Add section rendering parameter
    url.searchParams.set('section_id', 'section-rendering-product-card');

    try {
      const response = await fetch(url.toString(), {
        headers: {
          'Accept': 'text/html',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch bulk-order content: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      // Extract the bulk-order component
      const bulkOrder = html.querySelector('bulk-order-component');
      if (bulkOrder) {
        return /** @type {Element} */ (bulkOrder.cloneNode(true));
      }

      return null;
    } catch (error) {
      console.error('Failed to fetch bulk-order content:', error);
      return null;
    }
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the quick add modal with bulk-order content.
   * @param {Element} bulkOrderContent - The bulk-order component element
   */
  async updateQuickAddModal(bulkOrderContent) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!bulkOrderContent || !modalContent) return;

    // Wireframe: bulk-container > bulk-header (image + title) + bulk-variants (scrollable pills)
    const container = document.createElement('div');
    container.className = 'quick-add-modal__bulk-container';

    const header = document.createElement('div');
    header.className = 'quick-add-modal__bulk-header';

    const imageWrap = document.createElement('div');
    imageWrap.className = 'quick-add-modal__bulk-image';
    const productImageUrl = this.dataset.productImage;
    if (productImageUrl) {
      const img = document.createElement('img');
      img.src = productImageUrl;
      img.alt = this.dataset.productTitle || '';
      img.width = 80;
      img.height = 80;
      img.loading = 'lazy';
      imageWrap.appendChild(img);
    }
    header.appendChild(imageWrap);

    const titlePriceWrap = document.createElement('div');
    titlePriceWrap.className = 'quick-add-modal__bulk-title-wrap';

    const titleLink = document.createElement('a');
    titleLink.className = 'quick-add-modal__bulk-title';
    titleLink.textContent = this.dataset.productTitle || '';
    titleLink.href = this.productPageUrl;
    titlePriceWrap.appendChild(titleLink);

    const productPrice = this.dataset.productPrice;
    if (productPrice) {
      const priceEl = document.createElement('span');
      priceEl.className = 'quick-add-modal__bulk-price';
      priceEl.textContent = productPrice;
      titlePriceWrap.appendChild(priceEl);
    }

    header.appendChild(titlePriceWrap);

    container.appendChild(header);

    const variantsWrap = document.createElement('div');
    variantsWrap.className = 'quick-add-modal__bulk-variants';
    variantsWrap.appendChild(bulkOrderContent);
    container.appendChild(variantsWrap);

    // Clear and append instead of morph to preserve the modal content element
    modalContent.innerHTML = '';
    modalContent.appendChild(container);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
